import { closeOpenCards, createGame, selectCard, type Card, type GameState } from "./game";
import { generateLevel, type GeneratedLevel } from "./generator";

function resolveLevel(level: number | GeneratedLevel): GeneratedLevel {
  return typeof level === "number" ? generateLevel(level) : level;
}

/** Knows every country-capital identity, but still plays through the card-flip interface. */
export function perfectMemoryMoves(level: number | GeneratedLevel): number[] {
  const deal = resolveLevel(level);
  return deal.pairs.flatMap((pair) => {
    const country = deal.cards.find((card) => card.pairId === pair.id && card.kind === "country")!;
    const capital = deal.cards.find((card) => card.pairId === pair.id && card.kind === "capital")!;
    return [country.index, capital.index];
  });
}

export function runPerfectBot(level: number): GameState {
  let state = createGame(level);
  for (const index of perfectMemoryMoves(level)) state = selectCard(state, index);
  return state;
}

type NoKnowledgeEvent = { kind: "tap"; index: number } | { kind: "close" };

/** Remembers revealed faces but cannot identify a match until both ends have been seen. */
function noKnowledgeEvents(level: number): NoKnowledgeEvent[] {
  let state = createGame(level);
  const seen = new Map<number, Pick<Card, "pairId" | "kind">>();
  const events: NoKnowledgeEvent[] = [];
  const rememberOpen = () => {
    for (const index of state.open) {
      const card = state.cards[index]!;
      seen.set(index, { pairId: card.pairId, kind: card.kind });
    }
  };
  const findKnownMatch = (): [number, number] | undefined => {
    for (const [countryIndex, country] of seen) {
      if (country.kind !== "country" || state.cards[countryIndex]!.matched) continue;
      for (const [capitalIndex, capital] of seen) {
        if (capital.kind === "capital" && capital.pairId === country.pairId && !state.cards[capitalIndex]!.matched) {
          return [countryIndex, capitalIndex];
        }
      }
    }
    return undefined;
  };
  const firstUnknown = () => state.cards.find((card) => !card.matched && !card.faceUp && !seen.has(card.index))?.index;

  for (let guard = 0; state.phase === "playing" && guard < 10_000; guard++) {
    if (state.open.length === 2) {
      state = closeOpenCards(state);
      events.push({ kind: "close" });
    }
    rememberOpen();

    if (state.open.length === 0) {
      const known = findKnownMatch();
      if (known) {
        for (const index of known) {
          state = selectCard(state, index);
          events.push({ kind: "tap", index });
        }
        continue;
      }
      const index = firstUnknown();
      if (index === undefined) break;
      state = selectCard(state, index);
      events.push({ kind: "tap", index });
      rememberOpen();
    }

    if (state.open.length === 1) {
      const firstIndex = state.open[0]!;
      const first = state.cards[firstIndex]!;
      const counterpart = [...seen].find(([index, known]) => index !== firstIndex && !state.cards[index]!.matched && known.pairId === first.pairId && known.kind !== first.kind)?.[0];
      const next = counterpart ?? firstUnknown();
      if (next === undefined) break;
      state = selectCard(state, next);
      events.push({ kind: "tap", index: next });
      rememberOpen();
    }
  }
  return events;
}

/** Card indices chosen by the memory-only bot; pauses are handled by runNoKnowledgeBot. */
export function noKnowledgeMoves(level: number): number[] {
  return noKnowledgeEvents(level).flatMap((event) => event.kind === "tap" ? [event.index] : []);
}

export function runNoKnowledgeBot(level: number): GameState {
  let state = createGame(level);
  for (const event of noKnowledgeEvents(level)) {
    state = event.kind === "close" ? closeOpenCards(state) : selectCard(state, event.index);
  }
  return state;
}
